import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, EmptyState, PageHeader, formatUsd, useDocumentTitle } from "@unizpay/ui";
import { useState } from "react";
import { Link } from "react-router";
import { CreatePaymentLinkButton } from "../components/CreatePaymentLinkButton";
import { ConfirmDialog } from "../components/Dialog";
import { PaymentsBrowser } from "../components/PaymentsBrowser";
import { usePermission, useProfileApi } from "../lib/profile";
import { useSettlementSettings } from "../lib/queries";
import type { PaymentSummary } from "../lib/types";

/**
 * Every payment, from payment links and the API alike, filtered by source. Payment links are
 * created and deactivated here too.
 */
export function PaymentsPage() {
  useDocumentTitle("Payments");
  const { api, mode, key } = useProfileApi();
  const settings = useSettlementSettings();
  const ready = Boolean(settings.data?.active);
  const create = <CreatePaymentLinkButton settlementReady={ready} />;
  const canWrite = usePermission("payments.write");
  const queryClient = useQueryClient();
  const [deactivating, setDeactivating] = useState<PaymentSummary | null>(null);
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelPayment(mode, id),
    onSuccess: () => {
      setDeactivating(null);
      queryClient.invalidateQueries({ queryKey: key("payments") });
      queryClient.invalidateQueries({ queryKey: key("payment") });
    },
  });

  return (
    <>
      <PageHeader
        title="Payments"
        description={`Every ${mode} payment: payment links you share, and payments from your integration.`}
        actions={create}
      />

      {settings.data && !settings.data.active && !settings.data.pending && (
        <Alert tone="warn" title="Set up payouts to start accepting payments" className="mb-6">
          Payments cannot be created until you choose where funds should be delivered.{" "}
          <Link to="/settings/payouts">Open payout settings</Link>.
        </Alert>
      )}

      <PaymentsBrowser
        path="/payments"
        onDeactivate={canWrite ? setDeactivating : undefined}
        empty={
          <EmptyState title="No payments yet" action={create}>
            Create a payment link for an amount and send it to your customer, or create payments from your
            server with a {mode} API key (see{" "}
            <Link to="/developers/integration" className="text-accent hover:underline">
              Developers
            </Link>
            ).
          </EmptyState>
        }
      />

      <ConfirmDialog
        open={deactivating !== null}
        title="Deactivate this link?"
        confirmLabel="Deactivate"
        danger
        loading={cancel.isPending}
        error={cancel.error}
        onConfirm={() => deactivating && cancel.mutate(deactivating.id)}
        onCancel={() => {
          setDeactivating(null);
          cancel.reset();
        }}
      >
        {deactivating && (
          <>
            {deactivating.description ?? formatUsd(deactivating.amount, deactivating.currency)}: payers can no
            longer pay it. Funds that still arrive are detected as a late payment and delivered.
          </>
        )}
      </ConfirmDialog>
    </>
  );
}
